(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-lmx-root]';
  const instances = new WeakMap();

  const routeRoot = () => window.Shopify?.routes?.root || '/';
  const cartAddUrl = () => window.routes?.cart_add_url || `${routeRoot()}cart/add.js`;
  const cartUrl = () => window.routes?.cart_url || `${routeRoot()}cart`;

  const emit = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  };

  const safeJson = (node, fallback) => {
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      console.warn('[Linfatic MX] No se pudo leer JSON de variantes.', error);
      return fallback;
    }
  };

  const normalizeBoolean = (value) => value === true || value === 'true';

  class LinfaticMxLanding {
    constructor(root) {
      this.root = root;
      this.currency = root.dataset.currency || 'MXN';
      this.locale = document.documentElement.lang || 'es-MX';
      this.afterAdd = root.dataset.afterAdd || 'checkout';
      this.defaultBundle = root.dataset.defaultBundle || '1';
      this.variants = safeJson(root.querySelector('[data-lmx-variants]'), []);
      this.currentVariant = null;
      this.selectedBundle = null;
      this.loading = false;
      this.toastTimer = null;
      this.stickyObserver = null;
      this.mobileScrollHandler = null;

      this.variantInput = root.querySelector('[data-lmx-variant-id]');
      this.optionSelects = Array.from(root.querySelectorAll('[data-lmx-option]'));
      this.bundleInputs = Array.from(root.querySelectorAll('[data-lmx-bundle]'));
      this.addButtons = Array.from(root.querySelectorAll('[data-lmx-add]'));
      this.offer = root.querySelector('[data-lmx-offer]');
      this.message = root.querySelector('[data-lmx-message]');
      this.toast = root.querySelector('[data-lmx-toast]');
      this.stock = root.querySelector('[data-lmx-stock]');
      this.mobileSticky = root.querySelector('[data-lmx-mobile-sticky]');
      this.mainImage = root.querySelector('[data-lmx-main-image]');
      this.mainMedia = root.querySelector('[data-lmx-main-media]');

      this.init();
    }

    init() {
      this.bindBundleOptions();
      this.bindVariantOptions();
      this.bindAddButtons();
      this.bindGallery();
      this.bindFaqs();
      this.bindAnchors();
      this.initMobileSticky();
      this.syncInitialState();
      this.root.dataset.lmxInitialized = 'true';
    }

    destroy() {
      this.stickyObserver?.disconnect();
      if (this.mobileScrollHandler) {
        window.removeEventListener('scroll', this.mobileScrollHandler);
      }
      window.clearTimeout(this.toastTimer);
      this.root.dataset.lmxInitialized = 'false';
    }

    money(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(amount);
      } catch (_error) {
        return `$${amount.toFixed(2)}`;
      }
    }

    bindBundleOptions() {
      this.bundleInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          this.selectBundle(input);
        });

        input.closest('[data-lmx-bundle-card]')?.addEventListener('click', () => {
          if (!input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }

    bindVariantOptions() {
      this.optionSelects.forEach((select) => {
        select.addEventListener('change', () => {
          this.resolveVariantFromOptions();
        });
      });
    }

    bindAddButtons() {
      this.addButtons.forEach((button) => {
        button.addEventListener('click', () => this.addToCart());
      });
    }

    bindGallery() {
      const thumbnails = Array.from(this.root.querySelectorAll('[data-lmx-thumbnail]'));
      thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const src = thumb.dataset.src;
          if (!src || !this.mainImage) return;
          this.swapMainImage(src, thumb.dataset.alt || 'Linfatic');
          thumbnails.forEach((item) => item.classList.toggle('is-active', item === thumb));
        });
      });
    }

    bindFaqs() {
      this.root.querySelectorAll('[data-lmx-faq-button]').forEach((button) => {
        button.addEventListener('click', () => {
          const panelId = button.getAttribute('aria-controls');
          const candidate = panelId ? document.getElementById(panelId) : null;
          const panel = candidate && this.root.contains(candidate) ? candidate : null;
          if (!panel) return;

          const expanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
          panel.hidden = expanded;
        });
      });
    }

    bindAnchors() {
      this.root.querySelectorAll('a[href^="#"]').forEach((anchor) => {
        anchor.addEventListener('click', (event) => {
          const selector = anchor.getAttribute('href');
          if (!selector || selector === '#') return;
          const target = document.querySelector(selector);
          if (!target) return;
          event.preventDefault();
          target.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start'
          });
        });
      });
    }

    initMobileSticky() {
      if (!this.mobileSticky || !this.offer) return;

      const setVisible = (visible) => {
        this.mobileSticky.classList.toggle('is-visible', visible);
        this.mobileSticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
      };

      if ('IntersectionObserver' in window) {
        this.stickyObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          const passedHero = window.scrollY > 420;
          setVisible(!entry.isIntersecting && passedHero);
        }, { threshold: 0.12 });
        this.stickyObserver.observe(this.offer);
      } else {
        const onScroll = () => {
          const rect = this.offer.getBoundingClientRect();
          setVisible(window.scrollY > 420 && (rect.bottom < 0 || rect.top > window.innerHeight));
        };
        this.mobileScrollHandler = onScroll;
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
    }

    syncInitialState() {
      if (!Array.isArray(this.variants)) this.variants = [];

      const queryVariant = new URL(window.location.href).searchParams.get('variant');
      const variantFromUrl = queryVariant
        ? this.variants.find((variant) => String(variant.id) === String(queryVariant))
        : null;
      const variantFromInput = this.variantInput
        ? this.variants.find((variant) => String(variant.id) === String(this.variantInput.value))
        : null;

      this.currentVariant = variantFromUrl || variantFromInput || this.variants.find((variant) => variant.available) || this.variants[0] || null;
      this.applyVariantOptions(this.currentVariant);

      const checked = this.bundleInputs.find((input) => input.checked);
      const preferred = this.bundleInputs.find((input) => input.value === this.defaultBundle);
      this.selectBundle(checked || preferred || this.bundleInputs[0] || null, false);
      this.updateAll();
    }

    selectBundle(input, shouldUpdate = true) {
      if (!input) return;
      this.selectedBundle = input;
      input.checked = true;

      this.bundleInputs.forEach((bundle) => {
        const card = bundle.closest('[data-lmx-bundle-card]');
        card?.classList.toggle('is-selected', bundle === input);
      });

      if (shouldUpdate) this.updateAll();
    }

    applyVariantOptions(variant) {
      if (!variant || !this.optionSelects.length || !Array.isArray(variant.options)) return;
      this.optionSelects.forEach((select, index) => {
        const matchingOption = Array.from(select.options).find((option) => option.value === variant.options[index]);
        if (matchingOption) select.value = matchingOption.value;
      });
      this.updateOptionAvailability();
    }

    resolveVariantFromOptions() {
      const selected = this.optionSelects
        .sort((a, b) => Number(a.dataset.optionIndex || 0) - Number(b.dataset.optionIndex || 0))
        .map((select) => select.value);

      const exact = this.variants.find((variant) => {
        if (!Array.isArray(variant.options)) return false;
        return selected.every((value, index) => String(variant.options[index]) === String(value));
      });

      this.currentVariant = exact || null;
      this.updateOptionAvailability();
      this.updateAll();
    }

    updateOptionAvailability() {
      if (!this.optionSelects.length || !this.variants.length) return;
      const current = this.optionSelects
        .sort((a, b) => Number(a.dataset.optionIndex || 0) - Number(b.dataset.optionIndex || 0))
        .map((select) => select.value);

      this.optionSelects.forEach((select, index) => {
        Array.from(select.options).forEach((option) => {
          const candidate = [...current];
          candidate[index] = option.value;
          const exists = this.variants.some((variant) => {
            if (!variant.available || !Array.isArray(variant.options)) return false;
            return candidate.every((value, optionIndex) => {
              if (optionIndex === index) return String(variant.options[optionIndex]) === String(value);
              return !value || String(variant.options[optionIndex]) === String(value);
            });
          });
          option.disabled = !exists;
        });
      });
    }

    getBundleData(input = this.selectedBundle) {
      const quantity = Math.max(1, Number(input?.dataset.quantity || 1));
      const requestedDiscount = Math.max(0, Math.min(100, Number(input?.dataset.discount || 0)));
      const code = (input?.dataset.code || '').trim();
      // A visual discount is only applied when a real Shopify discount code is configured.
      const discount = code ? requestedDiscount : 0;
      return {
        quantity,
        discount,
        requestedDiscount,
        code,
        label: input?.dataset.label || `${quantity} unidad${quantity === 1 ? '' : 'es'}`
      };
    }

    getPricing(bundleData = this.getBundleData(), variant = this.currentVariant) {
      const unitPrice = Number(variant?.price || 0);
      const unitCompare = Number(variant?.compare_at_price || 0);
      const subtotal = unitPrice * bundleData.quantity;
      const total = Math.round(subtotal * (100 - bundleData.discount) / 100);
      const compareSubtotal = unitCompare > unitPrice ? unitCompare * bundleData.quantity : subtotal;
      const compare = total < subtotal ? Math.max(compareSubtotal, subtotal) : (compareSubtotal > subtotal ? compareSubtotal : 0);
      const unit = Math.round(total / bundleData.quantity);
      return { unitPrice, subtotal, total, compare, unit };
    }

    updateAll() {
      this.updateVariantState();
      this.updateBundlePrices();
      this.updatePurchaseSummary();
      this.updateVariantImage();
      this.updateUrl();
    }

    updateVariantState() {
      const available = Boolean(this.currentVariant?.available);
      if (this.variantInput) this.variantInput.value = this.currentVariant?.id || '';

      this.addButtons.forEach((button) => {
        button.disabled = !available || this.loading;
        button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
      });

      if (this.stock) {
        this.stock.classList.toggle('is-unavailable', !available);
        const textNode = Array.from(this.stock.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
        if (textNode) textNode.textContent = available ? ' Disponible' : ' Agotado';
      }
    }

    updateBundlePrices() {
      this.bundleInputs.forEach((input) => {
        const bundle = this.getBundleData(input);
        const pricing = this.getPricing(bundle);
        const card = input.closest('[data-lmx-bundle-card]');
        const totalNode = card?.querySelector('[data-lmx-bundle-total]');
        const unitNode = card?.querySelector('[data-lmx-bundle-unit]');
        if (totalNode) totalNode.textContent = this.money(pricing.total);
        if (unitNode) unitNode.textContent = this.money(pricing.unit);
      });
    }

    updatePurchaseSummary() {
      const bundle = this.getBundleData();
      const pricing = this.getPricing(bundle);
      const totalNodes = this.root.querySelectorAll('[data-lmx-total-price], [data-lmx-button-price], [data-lmx-sticky-price]');
      const compareNodes = this.root.querySelectorAll('[data-lmx-compare-price]');
      const stickyLabel = this.root.querySelector('[data-lmx-sticky-label]');

      totalNodes.forEach((node) => { node.textContent = this.money(pricing.total); });
      compareNodes.forEach((node) => {
        node.hidden = !pricing.compare || pricing.compare <= pricing.total;
        node.textContent = pricing.compare > pricing.total ? this.money(pricing.compare) : '';
      });
      if (stickyLabel) stickyLabel.textContent = bundle.label;

      if (bundle.requestedDiscount > 0 && !bundle.code) {
        this.root.dataset.lmxDiscountNeedsCode = 'true';
      } else {
        delete this.root.dataset.lmxDiscountNeedsCode;
      }
    }

    updateVariantImage() {
      const src = this.currentVariant?.featured_media?.preview_image?.src
        || this.currentVariant?.featured_image?.src
        || null;
      if (!src || !this.mainImage) return;

      const normalizedCurrent = this.mainImage.currentSrc || this.mainImage.src;
      if (normalizedCurrent && normalizedCurrent.split('?')[0] === src.split('?')[0]) return;
      this.swapMainImage(src, this.currentVariant?.featured_media?.alt || this.currentVariant?.name || 'Linfatic');
    }

    swapMainImage(src, alt) {
      if (!this.mainImage || !src) return;
      this.mainMedia?.classList.add('is-switching');
      const preload = new Image();
      preload.onload = () => {
        this.mainImage.src = src;
        this.mainImage.alt = alt || 'Linfatic';
        window.setTimeout(() => this.mainMedia?.classList.remove('is-switching'), 30);
      };
      preload.onerror = () => this.mainMedia?.classList.remove('is-switching');
      preload.src = src;
    }

    updateUrl() {
      if (!this.currentVariant?.id || !window.history?.replaceState || window.Shopify?.designMode) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.currentVariant.id);
      window.history.replaceState({}, '', url.toString());
    }

    setLoading(loading) {
      this.loading = loading;
      this.addButtons.forEach((button) => {
        button.classList.toggle('is-loading', loading);
        button.disabled = loading || !this.currentVariant?.available;
        button.setAttribute('aria-busy', loading ? 'true' : 'false');
      });
    }

    setMessage(text = '', type = '') {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.classList.toggle('is-error', type === 'error');
      this.message.classList.toggle('is-success', type === 'success');
    }

    showToast(text, type = 'success') {
      if (!this.toast) return;
      window.clearTimeout(this.toastTimer);
      this.toast.textContent = text;
      this.toast.classList.toggle('is-error', type === 'error');
      this.toast.classList.add('is-visible');
      this.toastTimer = window.setTimeout(() => this.toast?.classList.remove('is-visible'), 3500);
    }

    async addToCart() {
      if (this.loading) return;
      if (!this.currentVariant?.id || !this.currentVariant.available) {
        this.setMessage('Esta opción no está disponible en este momento.', 'error');
        return;
      }

      const bundle = this.getBundleData();
      const pricing = this.getPricing(bundle);
      this.setLoading(true);
      this.setMessage('');

      const drawer = document.querySelector('cart-drawer');
      const sections = drawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map((section) => section.id)
        : ['cart-drawer', 'cart-icon-bubble'];

      const payload = {
        items: [{
          id: Number(this.currentVariant.id),
          quantity: bundle.quantity,
          properties: {
            '_Linfatic - Rutina': bundle.label
          }
        }],
        sections,
        sections_url: window.location.pathname
      };

      try {
        const response = await fetch(cartAddUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'No pudimos agregar el producto al carrito.');
        }

        this.trackAddToCart(bundle, pricing);
        this.setMessage('Producto agregado correctamente.', 'success');

        const destination = this.getDestination();
        if (bundle.code) {
          const discountUrl = `${routeRoot()}discount/${encodeURIComponent(bundle.code)}?redirect=${encodeURIComponent(destination)}`;
          window.location.assign(discountUrl);
          return;
        }

        if (this.afterAdd === 'checkout') {
          window.location.assign(destination);
          return;
        }

        if (this.afterAdd === 'cart') {
          window.location.assign(cartUrl());
          return;
        }

        const rendered = this.renderCartDrawer(data);
        if (!rendered && this.afterAdd === 'drawer') {
          this.showToast('Agregado al carrito. Puedes continuar comprando.');
        } else {
          this.showToast('Producto agregado al carrito.');
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Ocurrió un error. Intenta nuevamente.';
        this.setMessage(message, 'error');
        this.showToast(message, 'error');
      } finally {
        this.setLoading(false);
      }
    }

    getDestination() {
      if (this.afterAdd === 'cart') return cartUrl();
      if (this.afterAdd === 'checkout') return `${routeRoot()}checkout`;
      return cartUrl();
    }

    renderCartDrawer(data) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && data.sections && typeof drawer.renderContents === 'function') {
        try {
          drawer.renderContents(data);
          return true;
        } catch (error) {
          console.warn('[Linfatic MX] No se pudo actualizar el carrito lateral.', error);
        }
      }

      if (data.sections?.['cart-icon-bubble']) {
        const currentBubble = document.querySelector('#cart-icon-bubble');
        if (currentBubble) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data.sections['cart-icon-bubble'], 'text/html');
          const replacement = doc.querySelector('#cart-icon-bubble');
          if (replacement) currentBubble.replaceWith(replacement);
        }
      }
      return false;
    }

    trackAddToCart(bundle, pricing) {
      const event = {
        event: 'linfatic_add_to_cart',
        ecommerce: {
          currency: this.currency,
          value: pricing.total / 100,
          items: [{
            item_id: String(this.currentVariant.id),
            item_name: this.root.dataset.productTitle || document.title,
            item_variant: this.currentVariant.title || '',
            price: pricing.unit / 100,
            quantity: bundle.quantity
          }]
        }
      };

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(event);
      emit('linfatic:add-to-cart', { event, variant: this.currentVariant, bundle });
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.lmxInitialized === 'true') return;
      const instance = new LinfaticMxLanding(root);
      instances.set(root, instance);
    });
  };

  const removeChromeClassIfUnused = () => {
    if (!document.querySelector(ROOT_SELECTOR)) {
      document.documentElement.classList.remove('lmx-hide-theme-chrome');
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
    window.setTimeout(removeChromeClassIfUnused, 0);
  });
})();
